import { useCallback, useEffect, useState } from 'react'

/**
 * Graceful-degradation data hook for the CRM screens. Tries the live API
 * fetcher; on ANY failure (backend not live yet → 404/401/405, network error,
 * or timeout) it silently falls back to the provided mock value so every screen
 * stays fully usable before Phase 3 is deployed. `live` reflects whether the
 * data came from the API.
 *
 * Once the backend is live the same screens transparently switch to real data
 * with no code change.
 */
/** STALE-WHILE-REVALIDATE (Lee, 26 Oct 2026 — "does it pull from the API
 *  every single time?": it did). Each call site's last result is kept
 *  in-memory for the browser session, keyed by the fetcher's source + deps.
 *  Returning to a page now renders the cached data INSTANTLY (no spinner)
 *  while the fresh fetch runs in the background and replaces it. In-memory
 *  only — a reload starts clean, so nothing ever goes stale across days. */
const swrCache = new Map<string, unknown>()

export function useCrm<T>(fetcher: () => Promise<T>, fallback: T, deps: unknown[] = []) {
  const cacheKey = `${fetcher.toString()}::${JSON.stringify(deps)}`
  const cached = swrCache.has(cacheKey) ? (swrCache.get(cacheKey) as T) : undefined
  const [data, setData] = useState<T>(cached !== undefined ? cached : fallback)
  const [loading, setLoading] = useState(cached === undefined)
  const [live, setLive] = useState(cached !== undefined)
  const [nonce, setNonce] = useState(0)

  /** Re-run the fetcher (e.g. after a create/update mutation). */
  const reload = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    let cancelled = false
    if (!swrCache.has(cacheKey)) setLoading(true)
    fetcher()
      .then((d) => {
        if (cancelled) return
        swrCache.set(cacheKey, d)
        setData(d)
        setLive(true)
      })
      .catch(() => {
        if (cancelled) return
        setData(fallback)
        setLive(false)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  return { data, loading, live, setData, reload }
}
